<template>
  <!-- #975 · Sin personas en formación (o sin acceso a Formación) no se dibuja nada: el formulario queda como antes. -->
  <div v-if="personas.length" class="col-100 bloque-tutor-practicas">
    <f7-block-title>Evaluación del tutor · A.04.01.PG-05</f7-block-title>
    <p class="bloque-tutor-nota">
      Se guarda aparte de la inspección, con sus propios botones: guardar la inspección no guarda ni firma esta evaluación.
      Las personas salen de la planificación <b>guardada</b> de la inspección.
    </p>
    <f7-card
      v-for="p in personas"
      :key="'practica-' + p.username"
      class="elevation-3 padding-half"
    >
      <f7-card-header class="bloque-tutor-cabecera">
        <span><b>{{ p.nombre || p.username }}</b> · {{ nombreRol(p.rol) }}</span>
        <span :class="'bloque-tutor-estado estado-' + estadoClave(p)">{{ textoEstado(p) }}</span>
      </f7-card-header>
      <f7-card-content>
        <p v-if="p.motivoNoVisible" class="bloque-tutor-motivo">{{ p.motivoNoVisible }}</p>
        <template v-else>
          <p v-if="p.rolEvaluado" class="bloque-tutor-aviso">
            Se evaluó como {{ nombreRol(p.rolEvaluado) }} y ahora figura como {{ nombreRol(p.rol) }}:
            esa evaluación no cuenta hasta que se revise y se vuelva a firmar.
          </p>

          <!-- Supervisado en formación: los siete aspectos y el dictamen -->
          <template v-if="esSupervisada(p)">
            <table class="bloque-tutor-aspectos">
              <thead>
                <tr>
                  <th scope="col">Aspecto evaluado</th>
                  <th scope="col" class="bloque-tutor-valoracion">Valoración</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="a in aspectos" :key="p.username + '-' + a.codigo">
                  <th scope="row">{{ a.descripcion }}</th>
                  <td class="bloque-tutor-valoracion">
                    <f7-segmented class="bloque-tutor-opciones">
                      <f7-button
                        v-for="v in valoraciones"
                        :key="a.codigo + '-' + v.codigo"
                        small
                        :active="borrador(p).aspectos[a.codigo] === v.codigo"
                        :disabled="!p.puedeEvaluar"
                        @click="marcarAspecto(p, a.codigo, v.codigo)"
                      >{{ v.etiqueta }}</f7-button>
                    </f7-segmented>
                  </td>
                </tr>
              </tbody>
            </table>
            <div class="bloque-tutor-fila">
              <span class="bloque-tutor-etiqueta">Dictamen: práctica satisfactoria</span>
              <f7-segmented class="bloque-tutor-opciones">
                <f7-button small :active="borrador(p).satisfactoria === true" :disabled="!p.puedeEvaluar" @click="fijar(p, 'satisfactoria', true)">Sí</f7-button>
                <f7-button small :active="borrador(p).satisfactoria === false" :disabled="!p.puedeEvaluar" @click="fijar(p, 'satisfactoria', false)">No</f7-button>
              </f7-segmented>
            </div>
          </template>

          <!-- Observador: basta la asistencia -->
          <div v-else class="bloque-tutor-fila">
            <span class="bloque-tutor-etiqueta">Asistió a la inspección como observador</span>
            <f7-segmented class="bloque-tutor-opciones">
              <f7-button small :active="borrador(p).asistencia === true" :disabled="!p.puedeEvaluar" @click="fijar(p, 'asistencia', true)">Sí</f7-button>
              <f7-button small :active="borrador(p).asistencia === false" :disabled="!p.puedeEvaluar" @click="fijar(p, 'asistencia', false)">No</f7-button>
            </f7-segmented>
          </div>

          <div class="bloque-tutor-fila">
            <span class="bloque-tutor-etiqueta">Fecha</span>
            <!-- Sin el .prevent, Intro aquí enviaría el formulario de la inspección que lo envuelve.
                 Y sin min ni max a propósito: la hoja global de rInput.vue pinta transparente el texto
                 de todo campo de fecha con límites y valor dentro de ellos, y la fecha elegida no se
                 veía (preview-10, 27/10). Que no sea posterior a hoy se dice junto a «Firmar». -->
            <input
              type="date"
              class="bloque-tutor-fecha"
              :value="borrador(p).fecha"
              :disabled="!p.puedeEvaluar"
              @input="fijar(p, 'fecha', $event.target.value || null)"
              @keydown.enter.prevent
            />
          </div>
          <div class="bloque-tutor-fila bloque-tutor-columna">
            <span class="bloque-tutor-etiqueta">Comentarios del tutor</span>
            <textarea
              class="bloque-tutor-comentarios"
              rows="3"
              :value="borrador(p).comentarios"
              :disabled="!p.puedeEvaluar"
              @input="fijar(p, 'comentarios', $event.target.value)"
            ></textarea>
          </div>

          <div class="bloque-tutor-fila">
            <span class="bloque-tutor-etiqueta">Tutor</span>
            <span>{{ textoTutor(p) }}</span>
          </div>
          <p v-if="p.revision" class="bloque-tutor-traza">
            Revisión {{ p.revision }} · creada por {{ p.createdBy }} el {{ fechaHora(p.createdAt) }}
            · última modificación de {{ p.updatedBy }} el {{ fechaHora(p.updatedAt) }}
          </p>

          <div class="bloque-tutor-acciones">
            <template v-if="p.puedeEvaluar">
              <f7-button small fill :disabled="!!guardando[p.username] || !hayCambios(p)" @click="guardar(p, false)">Guardar borrador</f7-button>
              <f7-button small fill color="green" :disabled="!!guardando[p.username] || !firmable(p)" @click="pedirFirma(p)">Firmar como {{ tutorNombre }}</f7-button>
              <span v-if="faltan(p).length" class="bloque-tutor-motivo">Para firmar falta {{ faltan(p).join(', ') }}.</span>
              <span v-else-if="firmadaSinCambios(p)" class="bloque-tutor-motivo">
                Ya está firmada. Para volver a firmarla, cambia algo y pulsa «Guardar borrador»: vuelve a borrador y se puede firmar de nuevo.
              </span>
              <span v-else-if="p.estado === 'FIRMADA' && hayCambios(p)" class="bloque-tutor-motivo">
                Hay cambios sin guardar: al guardarlos la evaluación vuelve a borrador y hay que firmarla otra vez.
              </span>
            </template>
            <span v-else class="bloque-tutor-motivo">{{ p.motivoNoEvaluable }}</span>
          </div>
          <p v-if="errores[p.username]" class="bloque-tutor-error">{{ errores[p.username] }}</p>
        </template>
      </f7-card-content>
    </f7-card>
  </div>
</template>

<script>
import moment from 'moment';
import PracticaTutor from '../../../js/db/PracticaTutor.js';

const ROLES = {
  OBSERVADOR: 'Observador',
  SUPERVISADO_FORMACION: 'Supervisado en formación'
};

export default {
  name: 'BloqueTutorPracticas',
  props: {
    expedienteId: { type: String, default: null },
    // Cambia cuando cambian los participantes de la inspección: es la señal para releer el
    // bloque (la API siempre decide con la planificación guardada, no con esta prop).
    firmaParticipantes: { type: String, default: '' }
  },
  data() {
    return {
      personas: [],
      aspectos: [],
      valoraciones: [],
      tutorNombre: '',
      borradores: {},
      // Revisión sobre la que se empezó cada borrador: es la que se manda al guardar, para que la
      // API rechace el guardado si otra persona ha guardado por medio en vez de pisarlo.
      bases: {},
      borradoresDe: null,
      guardando: {},
      errores: {},
      peticion: 0,
      // Recarga aún sin respuesta (0 si no hay ninguna).
      enCurso: 0,
      hoy: moment().format('YYYY-MM-DD')
    };
  },
  watch: {
    expedienteId: { immediate: true, handler() { this.cargar(); } },
    firmaParticipantes() { this.cargar(); }
  },
  methods: {
    cargar() {
      const id = this.expedienteId;
      const esta = ++this.peticion;
      if (id !== this.borradoresDe) {
        // Otra inspección: lo que hubiera sin guardar era de la anterior.
        this.borradores = {};
        this.bases = {};
        this.errores = {};
        this.personas = [];
        this.borradoresDe = id;
      }
      if (!id) {
        this.enCurso = 0;
        return;
      }
      this.enCurso = esta;
      PracticaTutor.deExpediente(id).then((res) => {
        // Una respuesta de otra inspección, o anterior a un guardado de evaluación, llega tarde y
        // no pinta nada.
        if (esta !== this.peticion) return;
        this.enCurso = 0;
        this.aspectos = res.aspectos || [];
        this.valoraciones = res.valoraciones || [];
        this.tutorNombre = res.tutorNombre || res.tutorUsername || '';
        this.personas = res.personas || [];
        this.conservarBorradores();
      }).catch(() => {
        // Sin acceso a Formación (403) o API sin este módulo (404): el bloque no existe para
        // esta persona, y un punto de entrada que no lleva a nada no se dibuja.
        if (esta !== this.peticion) return;
        this.enCurso = 0;
        this.personas = [];
      });
    },
    // Se relee al guardar la inspección, que es independiente de esta evaluación: lo que el tutor
    // tenga sin guardar, incluido lo que escriba mientras llega la respuesta, sigue ahí. Solo se
    // suelta el borrador de quien ya no está en la inspección o el que ya coincide con lo guardado.
    conservarBorradores() {
      const borradores = {};
      const bases = {};
      const errores = {};
      this.personas.forEach((p) => {
        const u = p.username;
        if (this.borradores[u] && this.hayCambios(p)) {
          borradores[u] = this.borradores[u];
          bases[u] = this.bases[u];
          errores[u] = this.errores[u] || null;
        }
      });
      this.borradores = borradores;
      this.bases = bases;
      this.errores = errores;
    },
    esSupervisada(p) {
      return p.rol === 'SUPERVISADO_FORMACION';
    },
    nombreRol(rol) {
      return ROLES[rol] || rol;
    },
    guardado(p) {
      return {
        asistencia: p.asistencia === undefined ? null : p.asistencia,
        aspectos: { ...(p.aspectos || {}) },
        satisfactoria: p.satisfactoria === undefined ? null : p.satisfactoria,
        comentarios: p.comentarios || '',
        fecha: p.fecha || null
      };
    },
    borrador(p) {
      return this.borradores[p.username] || this.guardado(p);
    },
    editar(p) {
      if (!this.borradores[p.username]) {
        this.$set(this.bases, p.username, p.revision || 0);
        this.$set(this.borradores, p.username, this.guardado(p));
      }
      return this.borradores[p.username];
    },
    fijar(p, campo, valor) {
      if (!p.puedeEvaluar) return;
      this.$set(this.editar(p), campo, valor);
    },
    marcarAspecto(p, codigo, valor) {
      if (!p.puedeEvaluar) return;
      this.$set(this.editar(p).aspectos, codigo, valor);
    },
    hayCambios(p) {
      const b = this.borradores[p.username];
      return !!b && JSON.stringify(b) !== JSON.stringify(this.guardado(p));
    },
    // Misma regla que la API (FormPracticaTutorService.pendientes): aquí solo sirve para decirlo
    // junto al botón antes de pulsar; quien decide es la API.
    faltan(p) {
      const b = this.borrador(p);
      const faltan = [];
      if (this.esSupervisada(p)) {
        const sinValorar = this.aspectos.filter((a) => !b.aspectos[a.codigo]).length;
        if (sinValorar) faltan.push(sinValorar === 1 ? 'valorar 1 aspecto' : 'valorar ' + sinValorar + ' aspectos');
        if (b.satisfactoria === null) faltan.push('el dictamen');
      } else if (b.asistencia === null) {
        faltan.push('indicar si asistió');
      }
      if (!b.fecha) faltan.push('la fecha');
      else if (b.fecha > this.hoy) faltan.push('una fecha que no sea posterior a hoy');
      return faltan;
    },
    // Firmada, con el rol con que se evaluó y sin nada pendiente de guardar: volver a firmarla solo
    // sumaba revisiones. La API lo rechaza igual (FormPracticaTutorService.mismaEvaluacion).
    firmadaSinCambios(p) {
      return p.estado === 'FIRMADA' && !p.rolEvaluado && !this.hayCambios(p);
    },
    firmable(p) {
      return this.faltan(p).length === 0 && !this.firmadaSinCambios(p);
    },
    estadoClave(p) {
      if (!p.revision) return 'vacia';
      return p.estado === 'FIRMADA' ? 'firmada' : 'borrador';
    },
    textoEstado(p) {
      if (!p.revision) return 'Sin evaluar';
      if (p.estado === 'FIRMADA') {
        return 'Firmada por ' + (p.tutorNombre || p.firmadaPor) + ' el ' + this.fechaHora(p.firmadaAt);
      }
      return 'Borrador sin firmar: no cuenta como evaluación';
    },
    textoTutor(p) {
      if (p.puedeEvaluar) {
        return p.revision && p.tutorNombre && p.tutorNombre !== this.tutorNombre
          ? p.tutorNombre + ' (si guardas, pasas a figurar tú: ' + this.tutorNombre + ')'
          : this.tutorNombre;
      }
      return p.tutorNombre || '—';
    },
    fechaHora(valor) {
      return valor ? moment(valor).format('DD/MM/YYYY HH:mm') : '';
    },
    pedirFirma(p) {
      const texto = 'Vas a firmar la evaluación de ' + (p.nombre || p.username) + ' como ' + this.tutorNombre
        + '. La firma queda registrada con tu usuario y la hora de ahora.';
      this.$f7.dialog.confirm(texto, 'Firmar evaluación', () => this.guardar(p, true));
    },
    guardar(p, firmar) {
      const b = this.borrador(p);
      const u = p.username;
      const datos = {
        revision: this.borradores[u] ? this.bases[u] : (p.revision || 0),
        asistencia: b.asistencia,
        aspectos: { ...b.aspectos },
        satisfactoria: b.satisfactoria,
        comentarios: b.comentarios,
        fecha: b.fecha
      };
      // Los campos siguen editables mientras se guarda: se compara al volver para no tirar lo
      // que se haya escrito entre tanto.
      const enviado = JSON.stringify(b);
      const expedienteId = this.expedienteId;
      this.$set(this.guardando, u, true);
      this.$set(this.errores, u, null);
      PracticaTutor.guardar(expedienteId, u, datos, firmar).then((res) => {
        if (expedienteId !== this.expedienteId) return;
        const i = this.personas.findIndex((x) => x.username === u);
        if (i >= 0) {
          // La respuesta trae la evaluación; lo que es de la vista (rol actual, permisos) se conserva.
          this.$set(this.personas, i, {
            ...this.personas[i],
            ...res,
            rol: this.personas[i].rol,
            rolEvaluado: null,
            puedeEvaluar: this.personas[i].puedeEvaluar,
            motivoNoEvaluable: this.personas[i].motivoNoEvaluable
          });
        }
        const actual = this.borradores[u];
        if (!actual || JSON.stringify(actual) === enviado) {
          this.$delete(this.borradores, u);
          this.$delete(this.bases, u);
        } else {
          // Lo escrito durante el guardado queda como cambio pendiente sobre la versión que se
          // acaba de guardar; si era una firma, el aviso de «vuelve a borrador» lo dice junto al botón.
          this.$set(this.bases, u, res && res.revision ? res.revision : (this.bases[u] || 0));
        }
        // Una recarga que salió antes de este guardado puede traer la revisión anterior y llegar
        // después: se descarta lanzando otra, que ya ve lo guardado. Los borradores no se tocan.
        if (this.enCurso) this.cargar();
      }).catch((e) => {
        if (expedienteId !== this.expedienteId) return;
        this.$set(this.errores, u, (e && e.message) || 'No se ha podido guardar la evaluación.');
      }).then(() => {
        this.$set(this.guardando, u, false);
      });
    }
  }
};
</script>

<style scoped>
.bloque-tutor-practicas { margin-top: 8px; }
.bloque-tutor-nota { margin: 0 16px 8px; font-size: 12px; color: #555; }
.bloque-tutor-cabecera { display: flex; justify-content: space-between; flex-wrap: wrap; gap: 8px; }
.bloque-tutor-estado { font-size: 12px; padding: 2px 8px; border-radius: 10px; background: #eee; }
.bloque-tutor-estado.estado-firmada { background: #e3f4e6; color: #1b5e20; }
.bloque-tutor-estado.estado-borrador { background: #fff3e0; color: #8a4b00; }
.bloque-tutor-aspectos { width: 100%; border-collapse: collapse; margin-bottom: 8px; }
.bloque-tutor-aspectos td, .bloque-tutor-aspectos th { padding: 4px; border-bottom: 1px solid #eee; font-size: 13px; }
.bloque-tutor-aspectos thead th { text-align: left; font-size: 12px; color: #555; }
.bloque-tutor-aspectos tbody th { text-align: left; font-weight: normal; }
.bloque-tutor-valoracion { width: 170px; }
/* Framework7 reparte el segmentado a partes iguales y con min-width 0: el «No» de la asistencia,
   más ancho que el «Sí», salía cortado como «N…». */
.bloque-tutor-opciones { flex: none; }
.bloque-tutor-opciones .button { min-width: 3.5em; }
.bloque-tutor-fila { display: flex; align-items: center; gap: 12px; margin: 8px 0; flex-wrap: wrap; }
.bloque-tutor-columna { flex-direction: column; align-items: stretch; }
.bloque-tutor-etiqueta { font-weight: 600; font-size: 13px; }
.bloque-tutor-fecha { border: 1px solid #ccc; border-radius: 4px; padding: 4px 6px; }
.bloque-tutor-comentarios { width: 100%; border: 1px solid #ccc; border-radius: 4px; padding: 6px; box-sizing: border-box; }
.bloque-tutor-traza { font-size: 11px; color: #777; margin: 4px 0; }
.bloque-tutor-acciones { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; margin-top: 8px; }
.bloque-tutor-motivo { font-size: 12px; color: #6d4c00; }
.bloque-tutor-aviso { font-size: 12px; color: #8a4b00; background: #fff3e0; padding: 6px 8px; border-radius: 4px; }
.bloque-tutor-error { font-size: 12px; color: #b00020; margin-top: 6px; }
</style>
